import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import React, { useState, useEffect } from "react";
import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Component
import SearchInput from "components/SearchInput";
import { SolidButton } from "components/buttons/SolidButton";
import TableField from "components/Table";
import ProfileMacAddressModal from './Modal';
import ConfirmDeleteModal from "./Modal/ConfirmDeleteModal";
import Loading from "./loading";
import GlobalAlert from "components/Notification";

// Util
import Api from "./util/api";
import { getTableColumns, fromJSON } from "./util/func";

const ProfileMacAddress = ({ showEditGeneral }) => {
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const columnHelper = createColumnHelper()

  //  State
  const { auth, isAuthReady } = useAuthScope();
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [uuidList, setUuidList] = useState({
    "read": uuidv4(),
    "create": uuidv4(),
    "update": uuidv4(),
    "delete": uuidv4(),
  });
  const [originMacAddresses, setOriginMacAddresses] = useState([]);
  const [macAddresses, setMacAddresses] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [selectedMacAddress, setSelectedMacAddress] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  // Method
  const fetchTableData = async () => {
    setLoading(true);
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.read);

    sendRequest(
      apiKey,
      uuidList.read,
      params,
    );
  }

  const handleSearchChange = (string) => {
    setSearchVal(string);

    if (string.trim() === "") {
      setMacAddresses(cloneDeep(originMacAddresses));
      return;
    }

    const filteredData = originMacAddresses.filter((item) => {
      const nameMatch = item.macGroupName.toLowerCase().includes(string.toLowerCase());
      const macMatch = item.ref.macList.some(macItem =>
        macItem.macAddress.toLowerCase().includes(string.toLowerCase())
      );
      return nameMatch || macMatch;
    });

    setMacAddresses(filteredData);
  }

  const createAction = () => {
    setOpenModal(true);
  }

  const handleCloseModal = () => {
    setSelectedMacAddress(null);
    setOpenModal(false);
    setUuidList((prev) => ({
      ...prev,
      create: uuidv4(),
      update: uuidv4(),
    }));
  }

  const handleSaveChanges = () => {
    handleCloseModal();
    fetchTableData();
  }

  const editMacAddressProfile = (data) => {
    setSelectedMacAddress(data);
    setOpenModal(true);
  }

  const deleteMacAddressProfile = (data) => {
    setSelectedMacAddress(data);
    setOpenRemoveModal(true);
  }

  const handleCloseRemoveModal = () => {
    setSelectedMacAddress(null);
    setOpenRemoveModal(false);
  }

  const handleDeleteMacAddress = () => {
    setLoading(true);
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.delete);

    sendRequest(
      apiKey,
      uuidList.delete,
      params,
      {
        profileMacAddrsIds: [selectedMacAddress.id],
      }
    );

    handleCloseRemoveModal();
  }

  // Side effect
  useEffect(() => {
    if (isAuthReady) {
      fetchTableData();
    }
  }, [isAuthReady])

  useEffect(() => {
    // Read
    const readResponse = getData(uuidList.read);

    if (readResponse) {
      if (readResponse.hasOwnProperty('code')) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: readResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        const updatedMacAddresses = [];
        const data = readResponse.data || [];

        for (let i = 0; i < data.length; i++) {
          const item = fromJSON(data[i]);
          updatedMacAddresses.push(item);
        }

        setOriginMacAddresses(cloneDeep(updatedMacAddresses));
        setMacAddresses(updatedMacAddresses);
        setLoading(false);
      }
    }

    // Delete
    const deleteResponse = getData(uuidList.delete);
    if (deleteResponse) {
      if (deleteResponse.hasOwnProperty("code") && deleteResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: deleteResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        fetchTableData();
      }
    }
  }, [dataMap]);

  // Because editMacAddressProfile & deleteMacAddressProfile need to declare before useTableInstance
  const columnsData = getTableColumns(theme, columnHelper, showEditGeneral, editMacAddressProfile, deleteMacAddressProfile);
  const table = useTableInstance({
    tableName: "profilesMacAddressTable",
    data: macAddresses,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });


  return <>
    <Box margin="20px">
      <Box
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "0 0 20px 0",
          gap: "20px",
        }}
      >
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            borderColor: theme.palette.primary.greyThread,
            minHeight: "50px",
            width: "1px",
            margin: "20px 20px",
          }}
        />

        <SearchInput
          value={searchVal}
          placeholder="Search by MAC Address, Name"
          onChange={handleSearchChange}
        />
        {
          showEditGeneral && <SolidButton
            startIcon={<AddIcon />}
            width="240px"
            height="32px"
            onClick={createAction}
          >
            Create New Port Profile
          </SolidButton>
        }
      </Box>

      <TableField
        {...table}
        pendingChildren={
          loading && <Loading />
        }
      />

      <ProfileMacAddressModal
        auth={auth}
        uuidList={uuidList}
        openModal={openModal}
        selectedMacAddress={selectedMacAddress}
        handleCloseModal={handleCloseModal}
        onSaveChanges={handleSaveChanges}
        showEditGeneral={showEditGeneral}
      />

      <ConfirmDeleteModal
        removeModal={openRemoveModal}
        onOpenRemoveModal={handleCloseRemoveModal}
        label={selectedMacAddress ? selectedMacAddress.macGroupName : ""}
        removeCallback={handleDeleteMacAddress}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  </>
};

export default ProfileMacAddress;